import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import type { SortingRule } from "react-table";
import useSWR from "swr";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { useAuthContext } from "../../../components/Auth";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import {
  PrimaryButtonWithPlusIcon,
  SecondaryButtonLargeV2,
} from "../../../components/Buttons/Buttons";
import { CreateNewTenantFormContainer } from "../../../components/CreateNewTenantForm/CreateNewTenantFormContainer";
import { Modal } from "../../../components/Modal/Modal";
import { createChangePerPageHandler } from "../../../components/Pagination/util";
import { TablePageLayout } from "../../../components/TablePage/TablePageLayout";
import { endpoints } from "../../../endpoints";
import {
  HorizontalButtonsContainer,
  PageWrapper,
} from "../../../layout/portalPageLayout";
import type {
  ITenantPaginatedOutputResponse,
  Tenant,
} from "../../../types/types";
import { useDebounce } from "../../../util/hooks";
import { useRoutePath } from "../../../util/Routing";
import {
  defaultHandleSort,
  DEFAULT_PER_PAGE,
  formatAddressCityState,
  getRelativeTime,
  makeUrlWithParams,
  useStoreState,
} from "../../../util/util";
import { SlideOut } from "../../../components/SlideOut/SlideOut";
import { DownloadReport } from "../../SharedPages/SellerOrdersListPage/DownloadReport/DownloadReport";
import type { ColumnDef } from "@tanstack/react-table";
import { spacingTokens } from "../../../designTokens";

type DistributorsTable = {
  name: string;
  location: string;
  id: string;
};

export const DistributorsListPage = () => {
  const user = useAuthenticatedUser();
  const { roleIsAgilisAdmin, roleIsSomeKindOfSeller } = useAuthContext();
  const { storefront_id } = useStoreState();

  const [query, setQuery] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });
  const perPageItems = [10, 20, 50, 100];
  const [perPage, setPerPage] = useState(query?.perPage ?? DEFAULT_PER_PAGE);
  const [showModal, setShowModal] = useState(false);
  const [tableData, setTableData] = useState<DistributorsTable[]>([]);
  const history = useHistory();
  const { adminPath } = useRoutePath();
  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const closeModal = () => setShowModal(false);
  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "asc",
  });

  const [offset, setOffset] = useState(query.offset ?? 0);
  const [pagination, setPagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });

  const [downloadReportSlideOut, setDownloadReportSlideOut] = useState(false);
  const closeDownloadReportSlideout = () => setDownloadReportSlideOut(false);

  const { data: tenantsResponse, error: tenantsError } = useSWR<
    ITenantPaginatedOutputResponse | undefined
  >(
    makeUrlWithParams(
      endpoints.v1_storefronts_id_tenants_id_tenants(
        storefront_id,
        user.tenant_id
      ),
      {
        offset: offset,
        limit: perPage,
        q: debouncedSearchQuery,
        // Don't let sort_by or order_by be empty strings.
        sort_by: sortingRules.sortBy || null,
        order_by: sortingRules.orderBy || "asc",
        tenant_type: "Distributor",
      }
    )
  );

  const handleSort = async (rules: SortingRule<object>[]) => {
    if (rules.length > 0) {
      const [rule, ...rest] = rules;
      const newRule = {
        id: rule.id === "location" ? "city" : rule.id,
        desc: rule.desc,
      };
      return defaultHandleSort(
        [newRule, ...rest],
        sortingRules,
        setSortingRules,
        setTableData
      );
    }
    return defaultHandleSort(
      rules,
      sortingRules,
      setSortingRules,
      setTableData
    );
  };

  useEffect(() => {
    if (tenantsResponse) {
      const { data: tenants, pagination } = tenantsResponse;

      setTableData(
        tenants.map((tenant: Tenant) => {
          const hqAddress = tenant.addresses.find(
            (address) => address.type === "Headquarters Address"
          );
          return {
            name: tenant.name,
            modified_at: getRelativeTime(tenant.modified_at),
            location: hqAddress ? formatAddressCityState(hqAddress) : "--",
            external_id: tenant.external_identity?.external_id || "--",
            id: tenant.id,
          };
        })
      );
      setPagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    }
  }, [tenantsResponse, setTableData, perPage]);

  const isLoading = !tenantsResponse && !tenantsError;

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOffset(0);
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: undefined });
    setOffset(0);
  };

  const handlePageClick = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const handleRowClick = (e: React.MouseEvent): void => {
    history.push(
      makeUrlWithParams(`${adminPath}/distributors/${e.currentTarget.id}`, {
        q: query.q,
        offset,
        perPage,
      })
    );
  };

  const handleCreateNew = () => {
    if (roleIsAgilisAdmin) {
      history.push(`${adminPath}/distributors/create`);
    } else {
      setShowModal(true);
    }
  };

  useEffect(() => {
    // This useEffect handles keeping the query in sync with the URL.
    if (debouncedSearchQuery === "") setQuery({ q: undefined });
    if (debouncedSearchQuery) {
      setQuery({ q: debouncedSearchQuery });
    }
  }, [setQuery, query, debouncedSearchQuery]);

  useEffect(() => {
    setQuery({ offset, perPage });
  }, [offset, perPage, setQuery]);

  const columns = useMemo<ColumnDef<DistributorsTable>[]>(
    () => [
      {
        accessorKey: "external_id",
        header: t("Distributor ID"),
      },
      {
        accessorKey: "name",
        header: t("Company Name"),
      },
      {
        accessorKey: "modified_at",
        header: t("Last Activity"),
        cell: (cell) => cell.renderValue(),
      },
      {
        accessorKey: "location",
        header: t("Location"),
        align: "right",
      },
      // TODO: these are commented out until the API supports it.
      // { accessorKey: "status", header: strings.status, Cell: statusCellFn },
    ],
    []
  );

  return (
    <PageWrapper>
      <Modal closeModal={closeModal} show={showModal}>
        <CreateNewTenantFormContainer
          sellerTenantId={user.tenant_id}
          type="Distributor"
          onSuccess={(tenantID) => {
            history.push(`${adminPath}/distributors/${tenantID}`);
            closeModal();
          }}
          onCancel={closeModal}
        />
      </Modal>
      <TablePageLayout
        showTitle
        title={t("Distributors")}
        searchQuery={searchQuery}
        searchPlaceholder={t("Search by company name")}
        onSearchChange={handleSearch}
        onSearchClear={handleClearSearch}
        toolbarActions={
          roleIsSomeKindOfSeller ? (
            <HorizontalButtonsContainer
              style={{ gap: spacingTokens.spacing_md }}
            >
              <SecondaryButtonLargeV2
                onClick={() => setDownloadReportSlideOut(true)}
              >
                {t("Download Report")}
              </SecondaryButtonLargeV2>
              <PrimaryButtonWithPlusIcon
                onClick={handleCreateNew}
                data-alignright
                useV2
              >
                <span style={{ fontSize: "15px" }}>{t("Create New")}</span>
              </PrimaryButtonWithPlusIcon>
            </HorizontalButtonsContainer>
          ) : undefined
        }
        perPageItems={perPageItems}
        perPage={perPage}
        onPerPageChange={changePerPage}
        columns={columns}
        data={tableData}
        isLoading={isLoading}
        error={tenantsError}
        rowClick={handleRowClick}
        handleSort={handleSort}
        pagination={pagination}
        totalCount={tenantsResponse?.pagination?.total}
        offset={offset}
        onPageChange={handlePageClick}
      >
        <SlideOut
          title={t("Download Report")}
          closeFlyout={closeDownloadReportSlideout}
          show={downloadReportSlideOut}
        >
          <DownloadReport
            closeSlideout={closeDownloadReportSlideout}
            transactionType={"distributor"}
          />
        </SlideOut>
      </TablePageLayout>
    </PageWrapper>
  );
};
